import {
  normalizeFrontmatterValues,
  normalizePublishingProfile,
  normalizeRelativePostPath,
  type FrontmatterValues,
  type PublishingProfile,
} from '../importExport/blogPublishing'
import { normalizeStatusId, normalizeSynopsis } from './pageStatus'
import { normalizePageSetup, type PageSetup } from '../../utils/pageSetup'

/** The encrypted, synced per-node metadata payload. */
export interface DocumentMetadata {
  aliases: string[]
  includeInExport: boolean
  includeInWordCount: boolean
  /** Pages: the base face inherited by text without an explicit font mark. */
  fontFamily?: string
  /** Pages: front matter values the writer set for a blog post. */
  frontmatter?: FrontmatterValues
  /** Pages: path of the published post, relative to the blog's publish folder. */
  publishedFile?: string
  /** Desks and folders: how pages inside are published to a static site. */
  publishing?: PublishingProfile
  /** Pages: a line or two on what the page is for, shown on the overview. */
  synopsis?: string
  /** Pages: the id of the page's status in Settings → Documents → Page Statuses. */
  status?: string
  /** Any node: the day it is due, 'YYYY-MM-DD'. Shown on the calendar and overview. */
  due?: string
  /** Pages: how the page view lays the document out on paper, and the default for export.
   *  Folders and desks: the setup last applied to everything inside, copied by new pages. */
  pageSetup?: PageSetup
}

/** Validates a metadata payload read from sync, a manifest, or an import. */
export function normalizeDocumentMetadata(raw: any): DocumentMetadata {
  const fontFamily = typeof raw?.fontFamily === 'string'
    ? raw.fontFamily.trim().slice(0, 160) || undefined
    : undefined
  return {
    aliases: Array.isArray(raw?.aliases) ? raw.aliases.filter((value: unknown) => typeof value === 'string') : [],
    includeInExport: raw?.includeInExport !== false,
    includeInWordCount: raw?.includeInWordCount !== false,
    fontFamily,
    frontmatter: normalizeFrontmatterValues(raw?.frontmatter),
    publishing: normalizePublishingProfile(raw?.publishing),
    publishedFile: normalizeRelativePostPath(raw?.publishedFile),
    synopsis: normalizeSynopsis(raw?.synopsis),
    status: normalizeStatusId(raw?.status),
    due: normalizeDueDate(raw?.due),
    pageSetup: normalizePageSetup(raw?.pageSetup),
  }
}

const DUE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function normalizeDueDate(raw: unknown): string | undefined {
  return typeof raw === 'string' && DUE_DATE_RE.test(raw) ? raw : undefined
}
